import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../components/container';
import ComboBoxProductType from '../../components/shared/hoc-product-type/ComboBoxProductType';
import { useLookupProductType } from '../../components/shared/hoc-product-type/useLookupProductType';
import { useLookupProduct } from '../../components/shared/hoc-product/useLookupProduct';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';
import { ProductTypeProductResponse } from '../../context/models/product-type/ProductTypeProductResponse';
import { ProductTypeProductActionTypes } from '../../context/stores/product-type-product/IProductTypeProductActionsTypes';
import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import ProductTypeProductModal from './ProductTypeProductModal';
import { useSelector } from 'react-redux';

type IProductTypeProductPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	product_type_id: number;
	allProductTypeProducts: ProductTypeProductResponse[];
	selectedIds: number[];
	showDetail: BaseShowDetail<ProductTypeProductResponse>;
	showConfirm: boolean;
	handleReloadAllData: (product_type_id: number) => void;
	handleSaveData: (data: ProductTypeProductResponse) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onChangeSelectedProductIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<ProductTypeProductResponse>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `sản phẩm`,
		status: state.productTypeProduct.status,
		product_type_id: parseInt(localStorage.getItem('product_type_id') || '1'),
		allProductTypeProducts: state.productTypeProduct.allProductTypeProducts,
		selectedIds: state.productTypeProduct.selectedIds,
		selectedProductIds: state.productTypeProduct.selectedIds,
		showDetail: state.productTypeProduct.showDetail,
		showConfirm: state.productTypeProduct.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<ProductTypeProductActionTypes>) => ({
	handleReloadAllData: (productTypeID: number) => {
		dispatch(actions.productTypeProduct.getAllRequest({ productTypeID }));
	},
	handleSaveData: (data: ProductTypeProductResponse) => {
		dispatch(actions.productTypeProduct.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.productTypeProduct.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.productTypeProduct.changeSelectedIds(ids));
	},
	onChangeSelectedProductIds: (ids: number[]) => {
		dispatch(actions.productTypeProduct.changeSelectedProductIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<ProductTypeProductResponse>) => {
		dispatch(actions.productTypeProduct.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.productTypeProduct.showConfirm(isShow));
	},
});

const ProductTypeProductPage = (props: IProductTypeProductPageProps) => {
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allProductTypeProducts,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		onChangeSelectedProductIds,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
		product_type_id,
	} = props;

	const { statusCat } = useSelector((state: AppState) => state.productTypeProduct);
	// const [listSelected, setListSelected] = useState<number[]>([]);

	const [showProductModal, setShowProductModal] = useState<boolean>(false);

	const dataSource = useMemo(() => {
		return allProductTypeProducts.sort((a, b) => a.stt - b.stt);
	}, [allProductTypeProducts]);

	const [productTypeID, setProductTypeID] = useState<number>(product_type_id);
	const { product_type_all } = useLookupProductType();

	var handleReloadData = useCallback(() => {
		handleReloadAllData(productTypeID);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productTypeID]);

	useEffect(() => {
		handleReloadData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productTypeID]);

	useEffect(() => {
		if (status === eBaseActionStatus.reload)
			handleReloadData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);


	const openProductModal = useCallback(() => {
		setShowProductModal(true);
	}, []);

	const columns = useMemo((): IDataTableColumn<ProductTypeProductResponse>[] => {
		return [
			{ accessorKey: 'stt', header: 'Số thứ tự', size: 100, meta: { align: 'center' } },
			{ accessorKey: 'name', header: 'Tên sản phẩm', minSize: 150 },
			{
				accessorKey: 'idProductType',
				header: 'Loại sản phẩm',
				minSize: 150,
				cell: ({ getValue }) => {
					const val = getValue() as number;
					const type = product_type_all.find((g: any) => g.id === val);
					return <span className='font-medium'>{type ? type.name : val}</span>;
				}
			},
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center' },
				size: 50,
				cell: () => (
					<ButtonLink onClick={openProductModal} title='Sửa' icon='ni ni-edit' theme='link' />
				)
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product_type_all, openProductModal]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>

							<li>
								<Button
									text='Thêm sản phẩm'
									icon='icon ni ni-plus'
									theme='primary'
									isDisabled={productTypeID <= 0}
									className='hidden md:inline-flex'
									onClick={() => setShowProductModal(true)}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						<li className='w-full lg:w-1/4'>
							<ComboBoxProductType
								name={'idProductType'}
								onValueChanged={(value) => {
									setProductTypeID(value ? parseInt(value) : 0);
								}}
								value={productTypeID}
								placeholder='Nhập loại sản phẩm...'
								sizeClass='xs'
							/>
						</li>
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={dataSource}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={dataSource.length}
								enableGlobalFilter={false}
								onRefresh={handleReloadData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showProductModal && (
				<>
					<ProductTypeProductModal
						isShow={showProductModal}
						onClose={() => setShowProductModal(false)}
						isSaving={false}
						productTypeId={product_type_id}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductTypeProductPage);
